import { useState } from "react";
import { Navigate } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { bootstrapPlatformOwner } from "@/lib/platform";

export default function PlatformBootstrap() {
  const { platformRole, profile } = useAuth();
  const [token, setToken] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (platformRole) return <Navigate to="/platform" replace />;

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await bootstrapPlatformOwner(token);
      toast.success("Platform owner access enabled");
      // Reload the session profile so the router sees the newly created
      // platform membership before entering the control plane.
      window.location.assign("/platform");
    } catch (error) {
      toast.error(error.message || "Could not enable platform access");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#F8F7F4] px-5 py-10 sm:px-8 lg:py-16">
      <div className="mx-auto grid w-full max-w-5xl overflow-hidden rounded-[2rem] border border-neutral-200 bg-white shadow-card lg:grid-cols-[1.05fr_0.95fr]">
        <section className="bg-neutral-900 p-7 text-white sm:p-10">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-white">
            <ShieldCheck size={26} weight="fill" />
          </span>
          <p className="mt-10 text-xs font-bold uppercase tracking-[0.16em] text-white/60">One-time setup</p>
          <h1 className="mt-3 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">Set up HoneyAcorn platform control</h1>
          <p className="mt-4 max-w-md text-sm leading-6 text-white/70">
            This activates the first platform owner: the person who can set up centers and their administrators across HoneyAcorn.
          </p>
          <ul className="mt-8 space-y-3 text-sm leading-6 text-white/75">
            <li className="flex gap-3">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
              No family, child, health, message, or payment records are exposed here.
            </li>
            <li className="flex gap-3">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
              The token is one-time only; an active owner prevents any later bootstrap.
            </li>
            <li className="flex gap-3">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
              Center roles remain separate and organization-scoped.
            </li>
          </ul>
        </section>

        <section className="p-7 sm:p-10">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">Confirm owner</p>
          <h2 className="mt-2 font-heading text-2xl font-semibold tracking-tight text-neutral-900">Use the protected bootstrap token</h2>
          <p className="mt-3 text-sm leading-6 text-neutral-600">
            Sign in as the intended owner, then paste the token stored in HoneyAcorn’s Railway backend configuration. It is never stored in this browser.
          </p>

          <div className="mt-6 rounded-2xl bg-[#F8F7F4] p-4">
            <p className="text-xs font-bold uppercase tracking-[0.12em] text-neutral-500">Signed-in account</p>
            <p className="mt-1 truncate text-sm font-semibold text-neutral-800">{profile?.email || "Your HoneyAcorn account"}</p>
          </div>

          <form className="mt-6 space-y-5" onSubmit={submit}>
            <label className="block">
              <span className="mb-1.5 block text-sm font-semibold text-neutral-800">Bootstrap token</span>
              <input
                autoComplete="off"
                className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2.5 font-mono text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                onChange={(event) => setToken(event.target.value)}
                placeholder="Paste one-time token"
                required
                spellCheck="false"
                type="password"
                value={token}
              />
            </label>
            <button
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-white transition hover:bg-primary-hover disabled:cursor-wait disabled:opacity-60"
              disabled={submitting}
              type="submit"
            >
              {submitting ? "Enabling…" : "Enable platform owner"}
              <ArrowRight size={17} weight="bold" />
            </button>
          </form>

          <p className="mt-5 text-xs leading-5 text-neutral-500">
            If an owner already exists or you do not have the token, contact the HoneyAcorn platform owner instead of using a center administrator account.
          </p>
        </section>
      </div>
    </main>
  );
}
